import { ChevronDown, Menu, MonitorPlay, Radio, Trophy, Users, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { Wordmark } from '@/features/landing/components/Wordmark'
import { signupsOpen } from '@/lib/env'
import { ctaClicked } from '@/lib/analytics'

/**
 * Build-time constant (Vite inlines import.meta.env). When false the two auth
 * buttons collapse to a single non-interactive "Coming soon" marker, and
 * /login and /signup render the coming-soon page — see App.tsx, which is where
 * the door is actually shut. This is the sign on the door.
 */
const SIGNUPS_OPEN = signupsOpen()

/**
 * Primary site nav. Lives on the Landing page + every /explore/* page +
 * /pricing so visitors can move between feature deep-dives without
 * bouncing back to root.
 *
 * The three explore buckets sit under a single "Features" dropdown
 * rather than three top-level items — keeps the top bar uncluttered,
 * matches the convention used by most marketing sites (Stripe, Linear,
 * Vercel), and gives the dropdown room for short sub-descriptions that
 * orient visitors before they click.
 *
 * Pricing stays standalone — it's a destination on its own, not part
 * of a feature taxonomy.
 */

type FeatureItem = {
  to: string
  label: string
  description: string
  icon: typeof Users
}

const FEATURE_ITEMS: FeatureItem[] = [
  {
    to: '/explore/squadrons',
    // Label is the discoverable-term bridge for visitors who haven't
    // learned the in-product vocabulary yet (we still call them
    // "squadrons" inside the app). Route stays /explore/squadrons.
    label: 'Clan/Guild Management',
    description: 'Roster, events, availability, Discord — where your squad actually lives.',
    icon: Users,
  },
  {
    to: '/explore/tournaments',
    label: 'Tournaments',
    description: 'Set up the tournament, take entries, and watch it play out on the big screen.',
    icon: Trophy,
  },
  {
    to: '/explore/live',
    label: 'Live',
    description: 'Link Twitch, schedule stream nights, and catch every streamer live in one feed.',
    icon: Radio,
  },
]

export function Nav() {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/60 bg-bg/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6">
        {/* Left: status pip + wordmark */}
        <Link to="/" className="flex items-center gap-3" aria-label="e404 home">
          <span
            className="relative inline-flex h-2 w-2 items-center justify-center"
            aria-hidden
          >
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
          </span>
          <Wordmark size="md" />
        </Link>

        {/* Center: Features dropdown + Pricing + Home (md+). Home is
            last because the wordmark already routes to "/" — the
            explicit menu item is a fallback for visitors who don't
            recognise the wordmark as clickable. `end` so it doesn't
            stay active on /explore/* / /pricing routes. */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
          <FeaturesDropdown />
          <NavLink
            to="/pricing"
            className={({ isActive }) =>
              `rounded-md px-3 py-2 font-display text-sm font-medium transition-colors ${
                isActive ? 'text-fg' : 'text-fg-muted hover:text-fg'
              }`
            }
          >
            Pricing
          </NavLink>
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `rounded-md px-3 py-2 font-display text-sm font-medium transition-colors ${
                isActive ? 'text-fg' : 'text-fg-muted hover:text-fg'
              }`
            }
          >
            Home
          </NavLink>
        </nav>

        {/* Right: auth actions + mobile menu trigger */}
        <div className="flex items-center gap-2">
          {SIGNUPS_OPEN ? (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden font-display sm:inline-flex"
              >
                <Link
                  to="/login"
                  onClick={() => ctaClicked('sign_in', 'nav', '/login')}
                >
                  Sign in
                </Link>
              </Button>
              <Button asChild variant="primary" size="sm">
                <Link
                  to="/signup"
                  onClick={() => ctaClicked('get_started', 'nav', '/signup')}
                >
                  Get started
                </Link>
              </Button>
            </>
          ) : (
            <Button asChild variant="outline" size="sm">
              {/* Links to /signup, which renders SignupsComingSoon while the
                  gate is shut — the waitlist lives there. Deliberately a
                  control, not a label: telling someone the door is closed and
                  giving them nothing to do is a dead end. */}
              <Link
                to="/signup"
                data-testid="signups-closed"
                onClick={() => ctaClicked('waitlist', 'nav', '/signup')}
              >
                Join the waitlist
              </Link>
            </Button>
          )}
          <MobileMenu />
        </div>
      </div>
    </header>
  )
}

/**
 * Mobile menu. Shown only on `<md` viewports — desktop has the
 * inline nav. Renders a full-screen sheet so links are big enough
 * to tap and there's room for the same sub-descriptions visitors
 * see in the desktop Features dropdown.
 *
 * Closes on: route change, ESC, backdrop tap, internal link tap.
 * Locks body scroll while open so the sheet doesn't bounce the
 * page under it.
 */
function MobileMenu() {
  const [open, setOpen] = useState(false)
  const location = useLocation()

  // Close on path change
  useEffect(() => setOpen(false), [location.pathname])

  // ESC handler + body-scroll lock
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        className="inline-flex size-10 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface hover:text-fg md:hidden"
      >
        <Menu className="size-5" aria-hidden />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] md:hidden" role="dialog" aria-modal="true">
          {/* Backdrop — tap-dismiss */}
          <button
            type="button"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            tabIndex={-1}
          />

          {/* Sheet */}
          <div className="absolute inset-x-0 top-0 flex max-h-dvh flex-col overflow-y-auto bg-bg shadow-2xl shadow-black/60">
            {/* Sheet header — mirrors top bar so visitor doesn't lose place */}
            <div className="flex h-16 items-center justify-between border-b border-border/60 px-6">
              <Link to="/" className="flex items-center gap-3" aria-label="e404 home">
                <span
                  className="relative inline-flex h-2 w-2 items-center justify-center"
                  aria-hidden
                >
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
                </span>
                <Wordmark size="md" />
              </Link>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="inline-flex size-10 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-surface hover:text-fg"
              >
                <X className="size-5" aria-hidden />
              </button>
            </div>

            {/* Features section */}
            <div className="px-4 pt-4">
              <p className="font-display px-3 text-[10px] font-extrabold tracking-[0.25em] text-fg-subtle uppercase">
                Features
              </p>
              <div className="mt-2 flex flex-col gap-1">
                {FEATURE_ITEMS.map((item) => {
                  const Icon = item.icon
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="flex items-start gap-3 rounded-lg p-3 transition-colors active:bg-surface"
                    >
                      <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-bg/40 text-primary">
                        <Icon className="size-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="font-display block text-sm font-bold text-fg">
                          {item.label}
                        </span>
                        <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">
                          {item.description}
                        </span>
                      </span>
                    </Link>
                  )
                })}
              </div>
            </div>

            {/* Standalone items */}
            <div className="border-t border-border/60 px-4 py-4">
              <NavLink
                to="/pricing"
                className={({ isActive }) =>
                  `font-display block rounded-lg p-3 text-sm font-bold transition-colors active:bg-surface ${
                    isActive ? 'text-fg' : 'text-fg-muted'
                  }`
                }
              >
                Pricing
              </NavLink>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `font-display block rounded-lg p-3 text-sm font-bold transition-colors active:bg-surface ${
                    isActive ? 'text-fg' : 'text-fg-muted'
                  }`
                }
              >
                Home
              </NavLink>
            </div>

            {/* Auth CTAs */}
            <div className="mt-auto flex flex-col gap-2 border-t border-border/60 px-4 py-4">
              {SIGNUPS_OPEN ? (
                <>
                  <Button asChild variant="primary" size="lg" className="w-full">
                    <Link
                      to="/signup"
                      onClick={() => ctaClicked('get_started', 'nav_mobile', '/signup')}
                    >
                      Get started free
                    </Link>
                  </Button>
                  <Button asChild variant="ghost" size="md" className="w-full font-display">
                    <Link
                      to="/login"
                      onClick={() => ctaClicked('sign_in', 'nav_mobile', '/login')}
                    >
                      Sign in
                    </Link>
                  </Button>
                </>
              ) : (
                <Button asChild variant="primary" size="lg" className="w-full">
                  <Link
                    to="/signup"
                    onClick={() => ctaClicked('waitlist', 'nav_mobile', '/signup')}
                  >
                    Join the waitlist
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * Features dropdown. Click-toggle (not hover) so it works on touch
 * devices. Closes on outside click, ESC, route change, or item
 * activation. "Features" reads as active when the visitor is on any
 * /explore/* path so they know which top-level they're inside.
 */
function FeaturesDropdown() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const isFeaturesActive = location.pathname.startsWith('/explore/')

  // Close on path change — covers the Link-click case + browser back/
  // forward navigation without each Link needing its own onClick.
  useEffect(() => setOpen(false), [location.pathname])

  // Outside-click + ESC handlers.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`font-display inline-flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
          isFeaturesActive || open ? 'text-fg' : 'text-fg-muted hover:text-fg'
        }`}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        Features
        <ChevronDown
          className={`size-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
          aria-hidden
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute top-full left-0 mt-2 w-[min(420px,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-surface/95 shadow-2xl shadow-black/40 backdrop-blur-xl"
        >
          <div className="flex flex-col gap-0 p-2">
            {FEATURE_ITEMS.map((item) => {
              const Icon = item.icon
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className="group flex items-start gap-3 rounded-lg p-3 transition-colors hover:bg-bg/60"
                >
                  <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-bg/40 text-primary transition-colors group-hover:border-primary/40 group-hover:bg-primary/10">
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-display block text-sm font-bold text-fg">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-fg-muted">
                      {item.description}
                    </span>
                  </span>
                </Link>
              )
            })}
          </div>
          <div className="border-t border-border/60 bg-bg/30 px-3 py-2">
            <Link
              to="/pricing"
              role="menuitem"
              className="font-display flex items-center justify-between rounded-md p-2 text-xs text-fg-muted transition-colors hover:bg-bg/60 hover:text-fg"
            >
              <span className="inline-flex items-center gap-2">
                <MonitorPlay className="size-3.5 text-fg-subtle" aria-hidden />
                See pricing for all tiers
              </span>
              <span className="text-fg-subtle">→</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
